import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, ImageOverlay, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { faMapMarkerAlt, faHome, faCrosshairs } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { renderToStaticMarkup } from 'react-dom/server';
import 'leaflet/dist/leaflet.css';
import { Container, Row, Col } from 'react-bootstrap';

const IndoorMap = React.memo(({ tracks = [], handleTrack, currentTrackSlug, imageUrl, language, metas }) => {
    const mapRef = useRef(null);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [initialZoom, setInitialZoom] = useState(1);
    const [bounds, setBounds] = useState(null);
    
    // Ref um den letzten currentTrackSlug zu tracken
    const lastCurrentTrackSlugRef = useRef(currentTrackSlug);

    const getImageDimensions = useCallback((imageUrl) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = imageUrl;
            img.onload = () => {
                const dimensions = { width: img.naturalWidth, height: img.naturalHeight };
                resolve(dimensions);
            };
            img.onerror = (err) => {
                console.error('Error loading image in IndoorMap:', err);
                reject(err);
            };
        });
    }, []);

    useEffect(() => {
        if (imageUrl) {
            getImageDimensions(imageUrl).then(dimensions => {
                setImageDimensions(dimensions);
                const initialBounds = L.latLngBounds([
                    [0, 0],
                    [dimensions.height, dimensions.width]
                ]);
                setBounds(initialBounds);
            }).catch(err => {
                console.error('Failed to load image dimensions in IndoorMap:', err);
            });
        }
    }, [imageUrl, getImageDimensions]);

    useEffect(() => {
        if (imageDimensions.width && imageDimensions.height && mapRef.current) {
            const map = mapRef.current;
            const fitZoomLevel = () => {
                const container = map._container;
                const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();
                const widthZoom = containerWidth / imageDimensions.width;
                const heightZoom = containerHeight / imageDimensions.height;
                return Math.min(widthZoom, heightZoom);
            };

            const zoomLevel = fitZoomLevel();
            setInitialZoom(zoomLevel * 0.5); // Set initial zoom to 50% of the calculated fit zoom level
            map.setView([imageDimensions.height / 2, imageDimensions.width / 2], zoomLevel * 0.5);
        }
    }, [imageDimensions]);

    const createIconMarkup = useCallback((icon, color) => {
        const htmlString = renderToStaticMarkup(
            <FontAwesomeIcon icon={icon} style={{ color: color, fontSize: '1em' }} />
        );
        const svgEncoded = encodeURIComponent(htmlString);
        return `data:image/svg+xml,${svgEncoded}`;
    }, []);

    // Memoize icon creation
    const customIcon = useMemo(() => {
        const customIconUrl = createIconMarkup(faMapMarkerAlt, "#ff0000");
        return new L.Icon({
            iconUrl: customIconUrl,
            iconSize: [31, 41],
            iconAnchor: [15.5, 41],
            popupAnchor: [0, -41],
            className: 'custom-icon',
        });
    }, [createIconMarkup]);

    const currentTrack = useMemo(() => {
        return tracks.find(track => track.slug === currentTrackSlug);
    }, [tracks, currentTrackSlug]);

    const getWaypoint = useCallback((track) => {
        if (!track?.metadata?.trackMeta_location) {
            return [imageDimensions.height / 2, imageDimensions.width / 2]; // Fallback zur Bildmitte
        }
        const locationData = track.metadata.trackMeta_location[1];
        try {
            const location = JSON.parse(locationData);
            return [location.x, location.y];
        } catch (error) {
            console.error('Error parsing location data:', error);
            return [imageDimensions.height / 2, imageDimensions.width / 2]; // Fallback zur Bildmitte
        }
    }, [imageDimensions]);

    // Memoized function um einen Track mit Standortdaten zu finden
    const findTrackWithLocation = useCallback(() => {
        // Zuerst current track prüfen
        if (currentTrack?.metadata?.trackMeta_location?.[1]) {
            return currentTrack;
        }
        
        // Wenn kein currentTrack mit Location, dann durch tracks iterieren
        for (const track of tracks) {
            if (track?.metadata?.trackMeta_location?.[1]) {
                return track;
            }
        }
        
        // Fallback: Kein Track mit Location gefunden
        return null;
    }, [currentTrack, tracks]);

    // Berechne initiale oder fallback Kartenposition basierend auf verfügbaren Daten
    const getMapCenter = useMemo(() => {
        const trackWithLocation = findTrackWithLocation();
        
        if (trackWithLocation) {
            // Track mit Location gefunden
            return getWaypoint(trackWithLocation);
        }
        
        // Fallback: Keine Location gefunden, nutze Bildmitte
        return [imageDimensions.height / 2, imageDimensions.width / 2];
    }, [findTrackWithLocation, getWaypoint, imageDimensions]);

    // FIXED: useEffect triggert nur bei echten currentTrackSlug Änderungen
    useEffect(() => {
        // Nur ausführen wenn sich currentTrackSlug tatsächlich geändert hat
        if (lastCurrentTrackSlugRef.current === currentTrackSlug) {
            return;
        }
        
        // Update der Referenz
        lastCurrentTrackSlugRef.current = currentTrackSlug;
        
        if (!mapRef.current) return;

        if (currentTrack?.metadata?.trackMeta_location?.[1]) {
            // Wenn der aktuelle Track Standortdaten hat, fliege direkt dorthin
            const locationData = currentTrack.metadata.trackMeta_location[1];
            try {
                const { x, y } = JSON.parse(locationData);
                // Moderateres Zoom-Level als vorher
                const currentZoom = mapRef.current.getZoom();
                mapRef.current.flyTo([x, y], currentZoom);
            } catch (error) {
                console.error('Error parsing location data for current track:', error);
            }
        } else {
            // Wenn der aktuelle Track keine Standortdaten hat, suche nach einem anderen Track mit Standortdaten
            const trackWithLocation = findTrackWithLocation();
            if (trackWithLocation && trackWithLocation !== currentTrack) {
                const position = getWaypoint(trackWithLocation);
                // Etwas auszoomen, da es ein Fallback ist
                const currentZoom = Math.max(1, mapRef.current.getZoom() - 0.5);
                mapRef.current.flyTo(position, currentZoom);
            }
        }
        
        mapRef.current.closePopup();
    }, [currentTrackSlug]); // Nur currentTrackSlug als Dependency!

    const getIcon = useCallback((isSelected, trackCoverUrl, storyCoverUrl) => new L.Icon({
        iconUrl: trackCoverUrl || storyCoverUrl || "/assets/illustrations/placeholder-cover.jpg",
        iconSize: isSelected ? [50, 50] : [30, 30],
        iconAnchor: isSelected ? [25, 25] : [15, 15],
        className: isSelected ? 'custom-icon selected-icon' : 'custom-icon',
    }), []);

    const handleCenterMap = useCallback(() => {
        if (mapRef.current) {
            mapRef.current.setView([imageDimensions.height / 2, imageDimensions.width / 2], initialZoom);
        }
    }, [imageDimensions, initialZoom]);

    const handleCenterTrack = useCallback(() => {
        if (mapRef.current && currentTrack?.metadata?.trackMeta_location) {
            try {
                const locationData = currentTrack.metadata.trackMeta_location[1];
                const { x, y } = JSON.parse(locationData);
                mapRef.current.setView([x, y], mapRef.current.getZoom());
            } catch (error) {
                console.error('Error parsing location data for centering track:', error);
            }
        } else {
            // Wenn kein aktueller Track mit Standort, versuche einen anderen Track zu finden
            const trackWithLocation = findTrackWithLocation();
            if (trackWithLocation && mapRef.current) {
                const position = getWaypoint(trackWithLocation);
                mapRef.current.setView(position, mapRef.current.getZoom());
            }
        }
    }, [currentTrack, findTrackWithLocation, getWaypoint]);

    // Memoize marker click handler
    const createMarkerClickHandler = useCallback((trackSlug) => {
        return () => {
            handleTrack(trackSlug);
            if (mapRef.current) {
                const track = tracks.find(t => t.slug === trackSlug);
                if (track) {
                    const waypoint = getWaypoint(track);
                    // Moderateres Zoom-Level beim Klicken
                    const currentZoom = mapRef.current.getZoom();
                    mapRef.current.flyTo([waypoint[0], waypoint[1]], currentZoom);
                }
            }
        };
    }, [handleTrack, tracks, getWaypoint]);

    return (
        <Container fluid>
            <Row>
                <Col>
                    <div className='title-page title-map title-indoormap'><h1>Übersichtskarte</h1></div>
                </Col>
            </Row>
            <Row className="map-container">
                <Col>
                    <MapContainer
                        center={getMapCenter}
                        zoom={initialZoom}
                        minZoom={0.01}  // Allow more zoom out
                        maxZoom={6}  // Increase maxZoom for more dynamic zooming
                        crs={L.CRS.Simple}
                        ref={mapRef}
                        className="h-100"
                    >
                        {bounds && (
                            <ImageOverlay
                                url={imageUrl}
                                bounds={bounds}
                            />
                        )}

                        {tracks.map((track) => (
                            <Marker
                                key={track.slug}
                                position={getWaypoint(track)}
                                icon={getIcon(
                                    track.slug === currentTrackSlug,
                                    track.metadata?.trackMeta_cover ? track.metadata.trackMeta_cover[language] : null,
                                    metas.storyMeta_cover ? metas.storyMeta_cover[language] : null
                                )}
                                zIndexOffset={track.slug === currentTrackSlug ? 1000 : 500}
                                eventHandlers={{
                                    click: createMarkerClickHandler(track.slug),
                                }}
                            >
                                <Popup>
                                    {track.metadata?.trackMeta_name ? track.metadata.trackMeta_name[language] : 'Unbenannter Track'}
                                </Popup>
                            </Marker>
                        ))}
                        
                        <div className="map-controls">
                            <button className="map-control-button" onClick={handleCenterMap} aria-label="Center map on home">
                                <FontAwesomeIcon icon={faHome} aria-hidden="true" />
                            </button>
                            <button className="map-control-button" onClick={handleCenterTrack} aria-label="Center map on current track">
                                <FontAwesomeIcon icon={faCrosshairs} aria-hidden="true" />
                            </button>
                        </div>
                    </MapContainer>
                </Col>
            </Row>
        </Container>
    );
}, (prevProps, nextProps) => {
    // Memoization für bessere Performance
    return prevProps.currentTrackSlug === nextProps.currentTrackSlug &&
           prevProps.tracks === nextProps.tracks &&
           prevProps.handleTrack === nextProps.handleTrack &&
           prevProps.imageUrl === nextProps.imageUrl &&
           prevProps.language === nextProps.language &&
           prevProps.metas === nextProps.metas;
});

export default IndoorMap;